import React, { useState } from 'react';
import axios from 'axios';

export default function ABDMPHRViewer() {
  const [patientId, setPatientId] = useState('');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedRecordType, setSelectedRecordType] = useState('all');
  const [selectedRecord, setSelectedRecord] = useState(null);

  const recordTypes = ['all', 'discharge_summary', 'opd_notes', 'ipd_notes', 'prescription', 'lab_report', 'imaging_report', 'vital_signs'];

  const handleLoadPHR = async () => {
    if (!patientId.trim()) {
      setError('Enter Patient ID!');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const response = await axios.get(`/api/abdm/patient/${patientId}/phr`);

      if (response.data.success) {
        setRecords(response.data.records || []);
        setSuccess(`✅ ${response.data.records_count} records found!`);
      } else {
        setError(response.data.error || 'Failed to load PHR');
      }
    } catch (err) {
      setError('PHR load failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadRecord = async (recordId, format = 'pdf') => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/abdm/record/${recordId}/download?format=${format}`);
      
      if (response.data.success) {
        const element = document.createElement('a');
        element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(JSON.stringify(response.data.export)));
        element.setAttribute('download', `record_${recordId}.${format}`);
        element.style.display = 'none';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
        
        setSuccess(`✅ Record downloaded!`);
      }
    } catch (err) {
      setError('Download failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const filteredRecords = selectedRecordType === 'all'
    ? records
    : records.filter(r => r.type === selectedRecordType);

  const getRecordIcon = (type) => {
    const icons = {
      'discharge_summary': '📋',
      'opd_notes': '🏥',
      'ipd_notes': '🛏️',
      'prescription': '💊',
      'lab_report': '🔬',
      'imaging_report': '🖼️',
      'vital_signs': '💓'
    };
    return icons[type] || '📄';
  };

  if (records.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="p-6 bg-white rounded-lg shadow-lg">
            <h2 className="text-2xl font-bold mb-4">📁 Patient का PHR Load करो</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Patient ID</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={patientId}
                    onChange={(e) => setPatientId(e.target.value)}
                    placeholder="patient_12345"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                    onKeyPress={(e) => e.key === 'Enter' && handleLoadPHR()}
                  />
                  <button
                    onClick={handleLoadPHR}
                    disabled={loading}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                  >
                    {loading ? 'Loading...' : '📁 Load PHR'}
                  </button>
                </div>
              </div>

              {success && <p className="p-3 bg-green-50 border border-green-200 rounded text-green-800">{success}</p>}
              {error && <p className="p-3 bg-red-50 border border-red-200 rounded text-red-800">{error}</p>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="p-4 bg-white rounded-lg shadow mb-6">
          <h3 className="font-bold mb-3">🔍 Filter Records</h3>
          <div className="flex flex-wrap gap-2">
            {recordTypes.map(type => (
              <button
                key={type}
                onClick={() => setSelectedRecordType(type)}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  selectedRecordType === type
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                {type === 'all' ? '📋 All' : `${getRecordIcon(type)} ${type.replace('_', ' ').toUpperCase()}`}
              </button>
            ))}
          </div>
        </div>

        <div className="p-4 bg-white rounded-lg shadow">
          <h3 className="font-bold mb-4">📅 Medical Timeline ({filteredRecords.length} records)</h3>

          {filteredRecords.length === 0 ? (
            <p className="text-gray-600 text-center py-8">कोई records नहीं मिले!</p>
          ) : (
            <div className="space-y-3">
              {filteredRecords.sort((a, b) => new Date(b.document_date) - new Date(a.document_date)).map((record, idx) => (
                <div
                  key={idx}
                  className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg cursor-pointer hover:shadow-lg"
                  onClick={() => setSelectedRecord(record)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">{getRecordIcon(record.type)}</span>
                        <span className="font-bold">{record.type.replace('_', ' ').toUpperCase()}</span>
                      </div>
                      <p className="text-sm text-gray-600">
                        📅 {new Date(record.document_date).toLocaleDateString()}
                      </p>
                      {record.facility_name && (
                        <p className="text-sm text-gray-600">🏥 {record.facility_name}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDownloadRecord(record.id, 'pdf'); }}
                        className="px-3 py-1 text-xs bg-blue-500 text-white rounded"
                      >
                        📥 PDF
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDownloadRecord(record.id, 'xml'); }}
                        className="px-3 py-1 text-xs bg-green-500 text-white rounded"
                      >
                        📥 XML
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => { setRecords([]); setPatientId(''); }}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            ← नया Patient देखो
          </button>
        </div>

        {selectedRecord && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-auto">
              <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
                <h3 className="text-xl font-bold">
                  <span>{getRecordIcon(selectedRecord.type)}</span> {selectedRecord.type.replace('_', ' ').toUpperCase()}
                </h3>
                <button onClick={() => setSelectedRecord(null)} className="text-2xl text-gray-500">✕</button>
              </div>
              <div className="p-6">
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <pre className="text-sm overflow-auto">{JSON.stringify(selectedRecord.content || selectedRecord, null, 2)}</pre>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleDownloadRecord(selectedRecord.id, 'pdf')} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded">📥 PDF</button>
                  <button onClick={() => handleDownloadRecord(selectedRecord.id, 'xml')} className="flex-1 px-4 py-2 bg-green-600 text-white rounded">📥 XML</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
